import { BackendQueryKeys, useTypeSafeMutation } from '@oresundsbron/api';
import { ContractType } from '@oresundsbron/api/src/backend/Contract';
import { useQueryClient } from '@tanstack/react-query';
import { pipe } from 'fp-ts/lib/function';
import {
  chain,
  fromOption,
  fromPredicate,
  getOrElseW,
  map,
  tryCatchK,
} from 'fp-ts/lib/TaskOption';
import { of } from 'fp-ts/lib/Task';
import {} from 'fp-ts/lib/Record';
import { useCallback } from 'react';
import { ValidationResult } from '@oresundsbron/use-form';
import { head } from 'fp-ts/lib/Array';
import { discountMainContract, useContractsStore } from '../stores/contracts';
import { useRouter } from 'next/router';

type useValidateCampaignParams = {
  contract: ContractType;
  t: (k: string) => string;
};

export function useValidateCampaign({
  contract,
  t,
}: useValidateCampaignParams) {
  const queryClient = useQueryClient();
  const { locale } = useRouter();
  const { mainCopy } = useContractsStore();

  const { mutateAsync } = useTypeSafeMutation(
    ['price', 'signup', 'validate'],
    (req) => req.Price.signup,
    {
      onSuccess: (result, vars) => {
        queryClient.setQueryData(
          BackendQueryKeys.PriceSignup(vars as unknown as {}),
          () => result
        );
      },
    }
  );

  return useCallback(
    (data: { currency: string; campaignCode: string }) =>
      pipe(
        {
          currency: data.currency,
          contractType: `${contract}`,
          campaignCode: data.campaignCode,
          locale: locale || '',
        },
        fromPredicate((x) => !!x.campaignCode),
        chain(tryCatchK(mutateAsync)),
        chain((res) => pipe(res, head, fromOption)),
        chain(
          fromPredicate(
            (x) =>
              x.contractType === mainCopy.value &&
              x.price !==
                mainCopy.prices.find((p) => p.currency === data.currency)
                  ?.price &&
              x.priceInclVAT !==
                mainCopy.prices.find((p) => p.currency === data.currency)
                  ?.priceInclVat
          )
        ),
        map((y) => {
          discountMainContract(y);
          return undefined;
        }),
        getOrElseW(() =>
          of({
            campaignCode: t('form.validations.campaignCode.invalid'),
          } as ValidationResult<{ campaignCode: string }>)
        )
      )(),
    [contract, mutateAsync, t, locale, mainCopy]
  );
}
